/* eslint-disable max-lines */
'use client';

import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { usePageHeader } from '@utils/hooks/use-page-header';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import TeamsUpdateForm from '@components/teams/teams-update-form.tsx';
import { Team, useGetTeams } from '@utils/api/teams/use-get-teams.tsx';
import { useCreateTeam } from '@utils/api/teams/use-create-team.tsx';
import { useDeleteTeam } from '@utils/api/teams/use-delete-team.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';

import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { PencilLine, Plus, Trash2 } from 'lucide-react';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import { PATHS } from '../../router/paths.ts';

type Row = {
  id: number;
  name: string;
  description: string;
};

type ValidationErr = { loc?: string[]; msg?: string };

function parseError(err: unknown, fallback: string): string {
  const data = (err as AxiosError<BaseError>)?.response?.data;
  if (Array.isArray(data?.detail)) {
    return (data.detail as ValidationErr[])
      .map(e => `${e.loc?.[1] ?? 'field'}: ${e.msg}`)
      .join('\n');
  }
  if (data?.message) return data.message;
  if (data?.error_description) return data.error_description;
  return fallback;
}

export default function Teams() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: teams, isLoading, isFetching, error } = useGetTeams();
  const createTeamMutation = useCreateTeam();
  const deleteTeamMutation = useDeleteTeam();

  const [selectedTeamId, setSelectedTeamId] = useState<number>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editTeam, setEditTeam] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (error && (error as AxiosError)?.response?.status === 403) {
      navigate(PATHS().HOME);
    }
  }, [error, navigate]);

  // Global search deep-links: `?edit=<id>` opens the update modal.
  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get('edit');

  useEffect(() => {
    if (!editId || !teams?.result) return;
    const match = teams.result.find((tt: Team) => String(tt.id) === editId);
    if (match) {
      setSelectedTeamId(match.id);
      setEditTeam(true);
    }
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      },
      { replace: true }
    );
  }, [editId, teams?.result, setSearchParams]);

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: t('name'),
        field: 'name',
        minWidth: 240,
        clampLines: 2,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'name',
        serverOperator: 'ilike',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => (
          <Text as="span" size="sm" weight="medium">
            {String(getValue() ?? '')}
          </Text>
        )
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: t('description'),
        field: 'description',
        minWidth: 320,
        clampLines: 2,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'description',
        serverOperator: 'ilike',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => {
          const v = String(getValue() ?? '');
          return (
            <Text as="span" size="sm" tone={v ? undefined : 'muted'}>
              {v || '—'}
            </Text>
          );
        }
      }
    ],
    [t]
  );

  const rows: Row[] = useMemo(
    () =>
      (teams?.result || []).map(({ id, name, description }: Team) => ({
        id,
        name,
        description: description ?? ''
      })),
    [teams]
  );

  const handleRowClick = (row: Row) => {
    if (!row?.id) return;
    setSelectedTeamId(row.id);
    setEditTeam(true);
  };

  const handleCreateTeam = () => {
    setIsSubmitting(true);

    createTeamMutation
      .mutateAsync({ name, description })
      .then(() => {
        toast.success(t('team-created-successfully'));
        setName('');
        setDescription('');
        setIsModalOpen(false);
      })
      .catch((err: unknown) => {
        toast.error(parseError(err, t('error-creating-team')));
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleDeleteTeam = (teamId: number) => {
    const toastId = toast.loading(t('deleting-team'));
    setIsDeleting(true);

    setTimeout(() => {
      deleteTeamMutation
        .mutateAsync(teamId)
        .then(() => {
          toast.success(t('team-deleted-successfully'), { id: toastId });
          setDeleteModal(false);
        })
        .catch((err: unknown) => {
          toast.error(
            parseError(err, t('error-deleting-team-please-try-again-later')),
            { id: toastId }
          );
        })
        .finally(() => setIsDeleting(false));
    }, 300);
  };

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <PencilLine className="h-3 w-3" />,
        intent: 'primary',
        run: (row: Row) => {
          setSelectedTeamId(row.id);
          setEditTeam(true);
        }
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-3.5 w-3.5" />,
        intent: 'danger',
        run: (row: Row) => {
          setSelectedTeamId(row.id);
          setDeleteModal(true);
        }
      }
    ],
    [t]
  );

  const showEmptyState =
    rows.length === 0 && !isLoading && !isFetching && !!teams;

  const FORM_ID = 'create-team-form';

  usePageHeader({
    title: t('teams'),
    description: t('teams-caption-editor'),
    actions: (
      <Button
        icon={<Plus width={14} />}
        label={t('new-team')}
        onClick={() => setIsModalOpen(true)}
      />
    )
  });

  return (
    <div className="bg-app min-h-screen">
      {/* Create modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="small"
        title={t('create-team')}
        caption={t('setup-your-team-name-and-description')}
        footerActions={[
          <Button
            key="cancel"
            type="button"
            onClick={() => setIsModalOpen(false)}
            label={t('cancel')}
            size="small"
            variant="smoke"
            disabled={isSubmitting}
          />,
          <Button
            key="create"
            type="submit"
            form={FORM_ID}
            label={isSubmitting ? t('creating') : t('create')}
            size="small"
            variant="primary"
            disabled={isSubmitting}
          />
        ]}
      >
        <form
          id={FORM_ID}
          className="mb-4 grid gap-4 w-full"
          onSubmit={e => {
            e.preventDefault();
            if (!isSubmitting) handleCreateTeam();
          }}
        >
          <button type="submit" className="hidden" aria-hidden="true" />
          <Input
            id="name"
            label={t('team-name')}
            placeholder={t('name')}
            onChange={e => setName(e.target.value)}
            type="text"
            value={name}
          />
          <Input
            id="description"
            label={t('team-description')}
            placeholder={t('description')}
            onChange={e => setDescription(e.target.value)}
            type="text"
            value={description}
          />
        </form>
      </Modal>

      <div className="px-4 sm:px-0 py-4 sm:pb-0 relative">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          filterMode="popover"
          enablePagination
          manualPagination
          totalItems={teams ? teams.total_results : undefined}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          height="88vh"
          dense
          grid={false}
          striped
          loading={isLoading || isFetching || !teams}
          getRowId={row => String(row.id)}
          onRowClick={handleRowClick}
        />

        {/* Empty state with create CTA */}
        {showEmptyState && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
            <div className="pointer-events-auto flex flex-col items-center gap-3 mt-8">
              <Text as="p" size="sm" tone="muted">
                {t('no-teams-yet')}
              </Text>
              <Button
                label={t('create-first-team')}
                icon={<Plus width={14} />}
                onClick={() => setIsModalOpen(true)}
                variant="primary"
                size="small"
              />
            </div>
          </div>
        )}
      </div>

      {selectedTeamId && editTeam && (
        <TeamsUpdateForm
          teamId={selectedTeamId}
          editTeam={editTeam}
          setEditTeam={setEditTeam}
        />
      )}

      {selectedTeamId && (
        <ConfirmationModal
          isOpen={deleteModal}
          onClose={() => setDeleteModal(false)}
          onConfirm={() => handleDeleteTeam(selectedTeamId)}
          size="small"
          title={t('delete-confirmation')}
          caption={t('are-you-sure-you-want-to-delete-this-team')}
          confirmLabel={isDeleting ? t('deleting') : t('delete')}
          cancelLabel={t('cancel')}
          confirmDisabled={isDeleting}
          destructive
          autoFocusConfirm
        />
      )}
    </div>
  );
}
